// Snapshot container: resolves which run to show (?run= param, else the
// app-wide mainRun || lastSuccessfulRun), finds the previous successful run
// as the comparison baseline, fetches the run comparison summary, and maps
// it into the SnapshotViewModel. Figures with no existing data source stay
// null and render as em dashes — never fabricated numbers.
import { useCallback, useContext, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useApi, useCustomer } from "../../contexts/app/provider";
import { useGetMyRunComparisonSummaryQuery } from "../../services/api/__generated__/backend_gateway-types";
import { VersionedDataContext } from "../../contexts/versioned_data/context";
import { Run } from "../../services/appsync/types";
import { useViewTitle } from "../../hooks/use_view_title";
import {
  convertToClientFeatures,
  type ClientFeatureFlags,
} from "../../types/feature_flags";
import dayjs from "../../utils/dayjs";
import { RunReportCardComponent } from "./component";
import { EnhancedComparisonData, KPI } from "./comparison_types";
import { useSnapshotLocations } from "./locations_data";
import { useDroppedLocationCheck } from "./data_quality";
import { useEngineManifest } from "./engine_manifest";
import { assessStaleness } from "./staleness";
import {
  SnapshotKpi,
  SnapshotPeriod,
  SnapshotTrailingMetric,
  SnapshotViewModel,
  SUPPLY_ABBREV,
  SUPPLY_UNIT_LABEL,
} from "./types";

const PERIOD_STORAGE_KEY = "atomic.runReportCard.period";

const WEEKS_PER_MONTH = 52 / 12;

const runSettingsInput = (run: Run | null) => ({
  inputId: run?.runSettings.inputId ?? "",
  runAttemptId: run?.runSettings.runAttemptId ?? "",
  settingsNameAndVersion: run?.runSettings.settingsNameAndVersion ?? "",
  workflowId: run?.runSettings.workflowId ?? "",
});

/** KPI from the comparison payload; drops the baseline when there is none. */
const metricOf = (
  kpi: KPI | null | undefined,
  hasBaseline: boolean,
  scale = 1
): { current: number | null; prev: number | null } => ({
  current: kpi ? kpi.current * scale : null,
  prev: hasBaseline && kpi?.previous != null ? kpi.previous * scale : null,
});

export const RunReportCardContainer = () => {
  useViewTitle("Run Report Card");
  const { apiService } = useApi();
  const { customerSettings } = useCustomer();
  const features: ClientFeatureFlags = useMemo(
    () => convertToClientFeatures(customerSettings?.features),
    [customerSettings]
  );
  const isHubAndSpoke = !!features?.InventoryHealthForHubs;

  const {
    loading: runsLoading,
    mainRun,
    lastSuccessfulRun,
    atomicPivotRuns,
  } = useContext(VersionedDataContext);

  const { search } = useLocation();
  const navigate = useNavigate();
  const requestedRunId = useMemo(
    () => new URLSearchParams(search).get("run"),
    [search]
  );

  // Period comes from the URL so a report card can be linked in either
  // state, falling back to the user's last choice, then Week.
  const urlPeriod = useMemo(() => {
    const raw = new URLSearchParams(search).get("period");
    return raw === "week" || raw === "month" ? (raw as SnapshotPeriod) : null;
  }, [search]);
  const period: SnapshotPeriod = useMemo(() => {
    if (urlPeriod) {
      return urlPeriod;
    }
    try {
      const stored = window.localStorage.getItem(PERIOD_STORAGE_KEY);
      if (stored === "week" || stored === "month") {
        return stored;
      }
    } catch {
      // Private browsing / storage disabled — fall through to the default.
    }
    return "week";
  }, [urlPeriod]);

  const setPeriod = useCallback(
    (next: SnapshotPeriod) => {
      try {
        window.localStorage.setItem(PERIOD_STORAGE_KEY, next);
      } catch {
        // Persisting is best-effort; the URL still carries the state.
      }
      const params = new URLSearchParams(search);
      params.set("period", next);
      navigate(`?${params.toString()}`, { replace: true });
    },
    [navigate, search]
  );

  // atomicPivotRuns is ordered newest-first (see decorateRuns).
  const completedRuns = useMemo(
    () => atomicPivotRuns.filter((run) => run.status === "RUN_COMPLETED"),
    [atomicPivotRuns]
  );

  const selectedRun = useMemo(
    () =>
      (requestedRunId &&
        completedRuns.find((run) => run.id === requestedRunId)) ||
      mainRun ||
      lastSuccessfulRun ||
      null,
    [requestedRunId, completedRuns, mainRun, lastSuccessfulRun]
  );

  // The previous *successful* run: the newest completed run older than the
  // selected one. Null for the oldest run — deltas are suppressed then.
  const previousRun = useMemo(() => {
    if (!selectedRun) {
      return null;
    }
    const selectedAt = new Date(selectedRun.createdAt).getTime();
    return (
      completedRuns.find(
        (run) => new Date(run.createdAt).getTime() < selectedAt
      ) ?? null
    );
  }, [completedRuns, selectedRun]);

  const hasBaseline = previousRun != null;

  // With no predecessor the endpoint still needs two runs; comparing the run
  // against itself yields correct current values and zero deltas, which we
  // suppress via hasBaseline.
  const { data, loading: summaryLoading } = useGetMyRunComparisonSummaryQuery({
    client: apiService.getClient(),
    skip: !selectedRun,
    variables: {
      runSettings1: runSettingsInput(selectedRun),
      runSettings2: runSettingsInput(previousRun ?? selectedRun),
    },
  });

  const comparison = useMemo<EnhancedComparisonData | null>(() => {
    const json = data?.getMyRunComparisonSummary;
    if (!json) {
      return null;
    }
    try {
      return JSON.parse(json) as EnhancedComparisonData;
    } catch {
      return null;
    }
  }, [data]);

  // The engine's data boundary, not the run's execution time.
  const { todayProxyMs } = useEngineManifest({ run: selectedRun });

  const stats = comparison?.enhanced_statistics ?? null;
  const alerts = stats?.alerts ?? null;

  const {
    locations,
    inbound,
    sales,
    rebalance,
    inTransitUnits,
    transferLanes,
    hubStockBySku,
    trailingActuals,
    networkInStock,
    openPoValue,
    channelForecast,
    channels,
    emptyLocationsHidden,
    monthLabel,
    monthWeeks,
    computedRisks,
    computedRisksReady,
    loading: locationsLoading,
  } = useSnapshotLocations({
    run: selectedRun,
    isHubAndSpoke,
    alerts,
    period,
    dataAsOfMs: todayProxyMs,
  });

  const coverageCheck = useDroppedLocationCheck({ run: selectedRun });

  const links = useMemo(
    () => ({
      inventory: isHubAndSpoke ? "/inventory-wos" : "/inventory",
      inventoryHealth: isHubAndSpoke
        ? "/inventory-health-for-hubs"
        : features?.InventoryHealthWithTransfers
        ? "/inventory-health-with-transfers"
        : "/inventory-health",
      supply: "/supply",
      transferPlanning: isHubAndSpoke
        ? "/transfer-recommendations-hub-to-spoke"
        : features?.TransferRecommendations
        ? "/transfer-recommendations"
        : "/transfer-planning",
    }),
    [isHubAndSpoke, features]
  );

  const model = useMemo<SnapshotViewModel | null>(() => {
    if (!selectedRun) {
      return null;
    }
    const kpisIn = stats?.kpis ?? null;
    const roleOf = (location: string) =>
      locations.find((l) => l.id === location || l.name === location)?.role ??
      null;

    const isMonth = period === "month";
    const inventoryValue = metricOf(kpisIn?.inventory_dollars, hasBaseline);
    // The summary reports months of supply natively; week mode converts.
    const supplyMetric = metricOf(
      kpisIn?.months_of_supply,
      hasBaseline,
      isMonth ? 1 : WEEKS_PER_MONTH
    );
    const inStockRate = metricOf(kpisIn?.in_stock_rate, hasBaseline);

    const inTransitTotal = isHubAndSpoke
      ? inTransitUnits
      : locations.reduce(
          (acc: number | null, l) =>
            l.inbound != null ? (acc ?? 0) + l.inbound : acc,
          null as number | null
        ) ?? inbound.units;

    // Each KPI prefers the comparison summary (it carries the previous-run
    // baseline for the delta chip) and falls back to figures computed from
    // the run's own transforms — value from inventory_forecast_master cost,
    // WOS from on-hand ÷ weekly outflow, in-stock demand-weighted from the
    // SKU-level walk.
    const onHandUnits = locations.reduce(
      (acc: number | null, l) => (l.units != null ? (acc ?? 0) + l.units : acc),
      null as number | null
    );
    const onHandValue = locations.reduce(
      (acc: number | null, l) => (l.value != null ? (acc ?? 0) + l.value : acc),
      null as number | null
    );
    // Fallback cover is on-hand ÷ outflow, built from the per-week RATE and
    // then converted to the selected period's unit. Dividing by the displayed
    // month figure instead would overstate cover early in the month, when that
    // figure is a to-date total covering only part of a month.
    const computedSupply =
      onHandUnits != null &&
      sales.weeklyRateUnits != null &&
      sales.weeklyRateUnits > 0
        ? onHandUnits / sales.weeklyRateUnits / (isMonth ? WEEKS_PER_MONTH : 1)
        : null;

    const kpis: SnapshotKpi[] = [
      {
        id: "inv_value",
        label: "Inventory value",
        current: inventoryValue.current ?? onHandValue,
        prev: inventoryValue.prev,
        unit: "$",
        note: isHubAndSpoke
          ? "On hand at cost, all locations"
          : "On hand at cost, across all DCs",
        linkPath: links.inventory,
        linkLabel: "Inventory",
        good: "none",
        missingReason:
          "No inventory value in the comparison summary or inventory_forecast_master",
      },
      {
        id: "wos",
        label: isMonth ? "Months of supply" : "Weeks of supply",
        current: supplyMetric.current ?? computedSupply,
        prev: supplyMetric.prev,
        unit: "w",
        unitLabel: SUPPLY_UNIT_LABEL[period],
        note:
          supplyMetric.current != null
            ? isMonth
              ? "Network · months of supply"
              : "Network · from months of supply × 52⁄12"
            : `Network · on hand ÷ actual ${isMonth ? "monthly" : "weekly"} outflow`,
        linkPath: links.inventoryHealth,
        linkLabel: "Inventory Health",
        good: "none",
        missingReason:
          "Needs either the comparison summary or on-hand + outflow figures",
      },
      {
        id: "in_stock",
        label: "In-stock rate",
        current: inStockRate.current ?? networkInStock,
        prev: inStockRate.prev,
        unit: "%",
        note: "SKU × location, as of run",
        linkPath: links.inventoryHealth,
        linkLabel: "Inventory Health",
        good: "up",
        missingReason:
          "No in-stock rate in the comparison summary or the SKU-level walk",
      },
      {
        id: "in_transit",
        label: isHubAndSpoke ? "Units in transit" : "Units inbound",
        current: inTransitTotal,
        prev: null,
        unit: "u",
        note: isHubAndSpoke
          ? "Hub→spoke, on trucks — counted in the total above"
          : "Placed POs, not yet delivered",
        linkPath: isHubAndSpoke ? links.transferPlanning : links.supply,
        linkLabel: isHubAndSpoke ? "Transfer Planning" : "Supply",
        good: "none",
        missingReason:
          "No in-transit source is exposed for this customer's runs yet",
      },
      {
        id: "on_order",
        label: "Open PO value",
        current: openPoValue,
        prev: null,
        unit: "$",
        note: "Placed, not yet received · at cost",
        linkPath: links.supply,
        linkLabel: "Supply",
        good: "none",
        missingReason:
          "No open (status=Ordered) purchase orders in this run's purchase_order_master",
      },
    ];

    // Prefer the comparison summary (it carries a baseline for the delta
    // chip); fall back to the actuals computed from sales_order_master.
    const revenueMetric = metricOf(kpisIn?.revenue, hasBaseline);
    const unitsMetric = metricOf(kpisIn?.units_shipped, hasBaseline);
    const trailing: SnapshotTrailingMetric[] = [
      {
        id: "revenue",
        label: "Revenue",
        current: revenueMetric.current ?? trailingActuals.revenue,
        prev: revenueMetric.prev,
        unit: "$",
        missingReason:
          "No revenue in the comparison summary or sales_order_master",
      },
      {
        id: "units",
        label: "Units shipped",
        current: unitsMetric.current ?? trailingActuals.units,
        prev: unitsMetric.prev,
        unit: "u",
        missingReason:
          "No shipped units in the comparison summary or sales_order_master",
      },
      {
        id: "fill",
        label: "Fill rate",
        current: null,
        prev: null,
        unit: "%",
        missingReason:
          "Fill rate is not in the run comparison summary or any wired transform yet",
      },
    ];

    // "Data as of" means the data boundary the engine used.
    const asOf = dayjs
      .utc(todayProxyMs ?? selectedRun.createdAt)
      .format("MM/DD/YYYY");

    return {
      isHubAndSpoke,
      period,
      channels,
      run: {
        displayId: selectedRun.displayId,
        name: selectedRun.name ?? null,
        status: selectedRun.status,
        finishedAt: dayjs
          .utc(selectedRun.updatedAt)
          .local()
          .format("MM/DD/YYYY HH:mm"),
        asOf,
        settingsName:
          selectedRun.runSettings.settingsNameAndVersion.split("|")[0],
        prevDisplayId: previousRun?.displayId ?? null,
      },
      kpis,
      inStockTrend: (stats?.trend_data?.in_stock_rate ?? [])
        .map((p) => p.value)
        .slice(-13),
      locations,
      sales,
      inbound,
      rebalance,
      transferLanes,
      trailing,
      // Risks prefer the rows computed from the run's own SKU-level walk
      // state — the CURRENT values at the as-of week. The comparison-summary
      // alerts carry extras (weeks-out, revenue at risk) but were measured
      // (staging, 2026-08-20) to contradict the run's walk: 13 of 20
      // cleanboss stock-out alerts had stock on hand, quantities off by up
      // to 10x, zero-quantity excess rows. Alerts remain only as a fallback
      // for runs whose walk tables never load.
      stockOuts: !computedRisksReady && alerts?.stock_outs.items.length
        ? alerts.stock_outs.items.map((a) => ({
            sku: a.sku,
            description: a.product_category || null,
            location: a.location,
            role: roleOf(a.location),
            wos: null,
            targetWos: null,
            weeksOut: a.weeks_out_of_stock,
            recentDemand: a.recent_demand_qty,
            revenueAtRisk: a.revenue_at_risk,
            hubCanCover: isHubAndSpoke
              ? hubStockBySku?.get(a.sku) ?? null
              : null,
            channelForecast:
              channelForecast?.get(a.sku + "|" + a.location) ?? null,
          }))
        : computedRisks.stockOuts.map((r) => ({
            sku: r.sku,
            description: r.skuName,
            location: r.location,
            role: roleOf(r.location),
            wos: r.wos,
            targetWos: r.target,
            weeksOut: null,
            recentDemand: r.recentDemand,
            revenueAtRisk: null,
            hubCanCover: isHubAndSpoke
              ? hubStockBySku?.get(r.sku) ?? null
              : null,
            channelForecast:
              channelForecast?.get(r.sku + "|" + r.location) ?? null,
          })),
      excess: !computedRisksReady && alerts?.excess_inventory.items.length
        ? alerts.excess_inventory.items.map((a) => ({
            sku: a.sku,
            description: a.product_category || null,
            location: a.location,
            role: roleOf(a.location),
            wos: null,
            targetWos: null,
            quantity: a.quantity,
            value: a.inventory_value,
            monthsOfSupply: a.months_of_supply,
            monthlyDemand: a.monthly_demand,
            channelForecast:
              channelForecast?.get(a.sku + "|" + a.location) ?? null,
          }))
        : computedRisks.excess.map((r) => ({
            sku: r.sku,
            description: r.skuName,
            location: r.location,
            role: roleOf(r.location),
            wos: r.wos,
            targetWos: r.target,
            quantity: r.quantity,
            value: r.value,
            monthsOfSupply: r.monthsOfSupply,
            monthlyDemand: r.monthlyDemand,
            channelForecast:
              channelForecast?.get(r.sku + "|" + r.location) ?? null,
          })),
      lowStock: !computedRisksReady && alerts?.low_stock.items.length
        ? alerts.low_stock.items.map((a) => ({
            sku: a.sku,
            description: a.product_category || null,
            location: a.location,
            role: roleOf(a.location),
            wos: null,
            targetWos: null,
            quantity: a.current_quantity,
            threshold: a.threshold_quantity,
            daysUntilStockout: a.days_until_stockout,
            channelForecast:
              channelForecast?.get(a.sku + "|" + a.location) ?? null,
          }))
        : computedRisks.lowStock.map((r) => ({
            sku: r.sku,
            description: r.skuName,
            location: r.location,
            role: roleOf(r.location),
            wos: r.wos,
            targetWos: r.target,
            quantity: r.quantity,
            threshold: r.threshold,
            daysUntilStockout: r.daysUntilStockout,
            channelForecast:
              channelForecast?.get(r.sku + "|" + r.location) ?? null,
          })),
      links,
    };
  }, [
    selectedRun,
    previousRun,
    hasBaseline,
    isHubAndSpoke,
    stats,
    alerts,
    locations,
    sales,
    inbound,
    rebalance,
    inTransitUnits,
    transferLanes,
    hubStockBySku,
    trailingActuals,
    networkInStock,
    openPoValue,
    channelForecast,
    computedRisks,
    computedRisksReady,
    todayProxyMs,
    links,
  ]);

  // How far the run's input data lags the run itself. Against the run's own
  // execution date, not wall clock — otherwise every historical run trips it.
  const staleness = useMemo(
    () =>
      assessStaleness({
        todayProxyMs,
        runCreatedAtMs: selectedRun
          ? new Date(selectedRun.createdAt).getTime()
          : null,
      }),
    [selectedRun, todayProxyMs]
  );

  const weekLabel = useMemo(() => {
    const asOf = todayProxyMs
      ? dayjs.utc(todayProxyMs)
      : selectedRun
      ? dayjs.utc(selectedRun.createdAt)
      : dayjs.utc();
    const daysSinceMonday = (asOf.day() + 6) % 7;
    return asOf.subtract(daysSinceMonday, "day").format("MMM D");
  }, [selectedRun, todayProxyMs]);

  return (
    <RunReportCardComponent
      loading={runsLoading || summaryLoading || locationsLoading}
      model={model}
      coverageCheck={coverageCheck}
      staleness={staleness}
      weekLabel={weekLabel}
      monthLabel={monthLabel}
      monthWeeks={monthWeeks}
      emptyLocationsHidden={emptyLocationsHidden}
      onPeriodChange={setPeriod}
    />
  );
};
